import { Box, Button, Chip, Dialog, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import { Organization, Voter } from '../../types';
import React, { useEffect, useState } from 'react';
import { Timestamp, collection, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { createIndex, removeIndexesOf } from '../../utils/indexer';
import { useNavigate, useParams } from 'react-router-dom';

import OrganizationListControl from '../Organizations/OrganizationListControl';
import { db } from "../../firebase";
import useAuth from '../../hooks/useAuth';

interface VoterFormProps {
}

const VoterForm: React.FC<VoterFormProps> = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [voter, setVoter] = useState<Voter>({
    id: '',
    name: '',
    precinct: '',
    purok: '',
    barangay: '',
    city_municipality: '',
    district: '',
    province: '',
    region: '',
    organizations: [],
    updatedAt: Timestamp.now(),
    updatedBy: user?.display_name ?? 'anonymous',
  });
  const [originalName, setOriginalName] = useState('');
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [isOrganizationDialogOpen, setIsOrganizationDialogOpen] = useState(false);

  const fetchVoter = async (id: string) => {
    try {
      const voterRef = doc(db, 'voters', id);
      const voterSnap = await getDoc(voterRef);
      
      if (voterSnap.exists()) {
        const fetchedVoter = voterSnap.data() as Voter;
        const updatedVoter = {
          id: voterSnap.id,
          name: fetchedVoter.name,
          precinct: fetchedVoter.precinct,
          purok: fetchedVoter.purok || '',
          barangay: fetchedVoter.barangay,
          city_municipality: fetchedVoter.city_municipality,
          district: fetchedVoter.district || '',
          province: fetchedVoter.province,
          region: fetchedVoter.region || '',
          organizations: fetchedVoter.organizations || [],
          updatedAt: fetchedVoter.updatedAt || Timestamp.now(),
          updatedBy: fetchedVoter.updatedBy || (user?.display_name ?? 'anonymous'),
        };
        setVoter(updatedVoter);
        setOriginalName(fetchedVoter.name); // Store the original name
      } else {
        setError('Voter not found');
      }
    } catch (error) {
      console.error('Error fetching voter:', error);
      setError('Error fetching voter');
    }
  };

  useEffect(() => {
    if (id) {
      fetchVoter(id);
    }
  }, [id]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsSaving(true);

    try {
      const updatedVoter = {
        ...voter,
        updatedAt: Timestamp.now(),
        updatedBy: user?.display_name ?? 'anonymous',
      };

      if (id) {
        const voterRef = doc(db, 'voters', id);
        await updateDoc(voterRef, updatedVoter);

        // Only recreate indexes if the name has changed
        if (updatedVoter.name !== originalName) {
          await removeIndexesOf("voter", "name", `voters/${id}`);
          await createIndex("voter", "name", updatedVoter.name, `voters/${id}`, user?.display_name ?? "anonymous");
        }
      } else {
        const votersRef = collection(db, "voters");
        const newVoterRef = doc(votersRef);
        await setDoc(newVoterRef, {
          ...updatedVoter,
          id: newVoterRef.id,
        });
        await createIndex("voter", "name", updatedVoter.name, newVoterRef.path, user?.display_name ?? "anonymous");

        const statsRef = doc(db, 'stats', 'voters');
        const statsDoc = await getDoc(statsRef);
        const totalCount = statsDoc.exists() ? statsDoc.data().totalCount || 0 : 0;        
        await updateDoc(statsRef, { totalCount: totalCount + 1 });
      }
      navigate('/voters');
    } catch (error) {
      console.error('Error saving voter:', error);
      setError('Error saving voter');
    } finally {
      setIsSaving(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVoter(prevVoter => ({
      ...prevVoter,
      [name]: value,
    }));
  };

  const handleOrganizationRemove = (orgToRemove: string) => {
    setVoter(prevVoter => ({
      ...prevVoter,
      organizations: prevVoter.organizations.filter(org => org !== orgToRemove)
    }));
  };

  const handleOpenOrganizationDialog = () => {
    setIsOrganizationDialogOpen(true);
  };

  const handleCloseOrganizationDialog = () => {
    setIsOrganizationDialogOpen(false);
  };

  const handleOrganizationSelect = (org: Organization) => {
    // Prevent duplicates
    if (!voter.organizations.some(o => o === org.name)) {
      setVoter(prevVoter => ({
        ...prevVoter,
        organizations: [...prevVoter.organizations, org.name]
      }));
    }
    handleCloseOrganizationDialog();
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: 600, margin: 'auto' }}>
      <Typography variant="h4">{id ? 'Edit Voter' : 'Add Voter'}</Typography>
      {error && <Typography color="error">{error}</Typography>}
      <TextField
        label="Name"
        name="name"
        value={voter.name}
        onChange={handleChange}
        required
      />
      <TextField
        label="Precinct"
        name="precinct"
        value={voter.precinct}
        onChange={handleChange}
        required
      />
      <TextField
        label="Purok"
        name="purok"
        value={voter.purok}
        onChange={handleChange}
      />
      <TextField
        label="Barangay"
        name="barangay"
        value={voter.barangay}
        onChange={handleChange}
        required
      />
      <TextField
        label="City/Municipality"
        name="city_municipality"
        value={voter.city_municipality}
        onChange={handleChange}
        required
      />
      <TextField
        label="District"
        name="district"
        value={voter.district}
        onChange={handleChange}
        required
      />
      <TextField
        label="Province"
        name="province"
        value={voter.province}
        onChange={handleChange}
        required
      />
      <TextField
        label="Region"
        name="region"
        value={voter.region}
        onChange={handleChange}
        required
      />
      <Typography variant="subtitle1">Organizations</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {voter.organizations.map((org) => (
            <Chip
              key={org}
              label={org}
              onDelete={() => handleOrganizationRemove(org)}
            />
          ))}
        </Box>
        <Button 
          variant="outlined" 
          onClick={handleOpenOrganizationDialog}
          sx={{ mt: 1 }}
        >
          Add Organizations
        </Button>
      </Box>

      <Button type="submit" variant="contained" disabled={isSaving}>
        {id ? 'Update Voter' : 'Add Voter'}
      </Button>

      <Dialog 
        open={isOrganizationDialogOpen} 
        onClose={handleCloseOrganizationDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Organizations</DialogTitle>
        <DialogContent>
          <OrganizationListControl 
            onSelect={handleOrganizationSelect}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default VoterForm;
